/**
 * Section B2 Page
 * B.2.適用性
 * 三頁籤 + DataTable CRUD 模式
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import sectionB2Service, { SectionB2Detail, SectionB2Create, SectionB2Update } from '../services/sectionB2Service';
import RichTextEditor from '../components/RichTextEditor';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';
import '../styles/DataTable.css';

const SectionB2Page: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { selectedProjectId } = useProject();
  const toast = useToast();
  const hasInitialized = useRef(false);

  // ========== 頁籤 ==========
  const [activeTab, setActiveTab] = useState<number>(1);

  // ========== 資料狀態 ==========
  const [allData, setAllData] = useState<SectionB2Detail[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ========== Modal 狀態 ==========
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<SectionB2Detail | null>(null);
  const [formData, setFormData] = useState<{
    applicability_codition: string;
    project_compliance: string;
  }>({
    applicability_codition: '',
    project_compliance: ''
  });

  // ========== 分頁 ==========
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // ========== 頁籤定義 ==========
  const tabs = [
    { key: 1, label: i18n.language === 'zh-TW' ? '方法論適用說明' : 'Methodology applicability' },
    { key: 2, label: i18n.language === 'zh-TW' ? '工具適用說明' : 'Tool applicability' },
    { key: 3, label: i18n.language === 'zh-TW' ? '締約方需求' : 'Host Party requirements' }
  ];

  // ========== 初始化 ==========
  useEffect(() => {
    if (!hasInitialized.current && !permissionLoading) {
      validateSession('SectionB2Page');
      hasInitialized.current = true;
    }
  }, [permissionLoading]);

  // ========== 專案變更時載入資料 ==========
  useEffect(() => {
    if (!selectedProjectId) {
      setAllData([]);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await sectionB2Service.getListByProject(selectedProjectId);
        setAllData(data);
      } catch (err: any) {
        console.error('載入 B.2 資料失敗:', err);
        if (err.response?.status === 404 || err.response?.status === 500) {
          setAllData([]);
        } else {
          setError(err.response?.data?.detail || (i18n.language === 'zh-TW' ? '載入資料失敗' : 'Failed to load data'));
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedProjectId, i18n.language]);

  // ========== 按頁籤過濾資料 ==========
  const filteredData = allData.filter(item => item.mothed_references === activeTab);

  // ========== 分頁計算 ==========
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredData.slice(startIndex, startIndex + itemsPerPage);

  // 切換頁籤時重置分頁
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  // ========== Modal 操作 ==========
  const openModal = (item?: SectionB2Detail) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        applicability_codition: item.applicability_codition || '',
        project_compliance: item.project_compliance || ''
      });
    } else {
      setEditingItem(null);
      setFormData({
        applicability_codition: '',
        project_compliance: ''
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  // ========== CRUD 操作 ==========
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProjectId) return;

    try {
      if (editingItem) {
        const updateData: SectionB2Update = {
          applicability_codition: formData.applicability_codition || undefined,
          project_compliance: formData.project_compliance || undefined
        };
        await sectionB2Service.update(editingItem.id, updateData);
        toast.success(t('message.saveSuccess'));
      } else {
        const createData: SectionB2Create = {
          project_id: selectedProjectId,
          mothed_references: activeTab,
          applicability_codition: formData.applicability_codition || undefined,
          project_compliance: formData.project_compliance || undefined
        };
        await sectionB2Service.create(createData);
        toast.success(t('message.createSuccess'));
      }

      closeModal();
      const data = await sectionB2Service.getListByProject(selectedProjectId);
      setAllData(data);
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || t('common.error');
      toast.error(errorMsg);
    }
  };

  const handleDelete = async (item: SectionB2Detail) => {
    const ok = await toast.confirm({
      title: i18n.language === 'zh-TW' ? '刪除確認' : 'Delete Confirmation',
      message: i18n.language === 'zh-TW' ? '確定要刪除此筆資料嗎？' : 'Are you sure you want to delete this record?',
      confirmText: i18n.language === 'zh-TW' ? '刪除' : 'Delete',
      severity: 'error',
    });
    if (!ok) {
      return;
    }

    try {
      await sectionB2Service.delete(item.id);
      toast.success(t('message.deleteSuccess'));
      if (selectedProjectId) {
        const data = await sectionB2Service.getListByProject(selectedProjectId);
        setAllData(data);
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || t('common.error');
      toast.error(errorMsg);
    }
  };

  // ========== 權限檢查 ==========
  if (permissionLoading) {
    return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  }

  if (!hasPermission('section_b2', 'read')) {
    return (
      <div className="page-container">
        <div className="error-message">{t('common.noPermission')}</div>
      </div>
    );
  }

  const canCreate = hasPermission('section_b2', 'create');
  const canUpdate = hasPermission('section_b2', 'update');
  const canDelete = hasPermission('section_b2', 'delete');

  const activeTabLabel = tabs.find(tab => tab.key === activeTab)?.label || '';

  // ========== 渲染 ==========
  return (
    <div className="page-container">
      <div className="page-header">
        <FunctionPageHeader funcCode="section_b2" />
      </div>

      {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}

      {selectedProjectId && (
        <div>
          {/* 頁籤 */}
          <div style={{ display: 'flex', borderBottom: '2px solid #e0e0e0', marginBottom: '16px' }}>
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderBottom: activeTab === tab.key ? '3px solid #4A90E2' : '3px solid transparent',
                  background: activeTab === tab.key ? '#f0f7ff' : 'transparent',
                  color: activeTab === tab.key ? '#4A90E2' : '#666',
                  fontWeight: activeTab === tab.key ? 600 : 400,
                  cursor: 'pointer',
                  fontSize: '14px',
                  marginBottom: '-2px'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* 新增按鈕 */}
          {canCreate && (
            <div style={{ marginBottom: '12px', textAlign: 'right' }}>
              <button className="btn-primary" onClick={() => openModal()}>
                {t('common.create')}
              </button>
            </div>
          )}

          {/* DataTable */}
          {loading ? (
            <div className="loading">{t('common.loading')}</div>
          ) : filteredData.length === 0 ? (
            <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: '16px' }}>
              {i18n.language === 'zh-TW'
                ? `尚無${activeTabLabel}資料，請點擊「新增」按鈕新增資料`
                : `No ${activeTabLabel} data yet. Click "Create" to add a new record.`}
            </div>
          ) : (
            <>
              <div className="data-table-container">
                <table className="data-table">
                  <thead className="table-header-dark-green">
                    <tr>
                      <th style={{ width: '45%' }}>
                        {i18n.language === 'zh-TW' ? '適用要求' : 'Applicability condition'}
                      </th>
                      <th style={{ width: '45%' }}>
                        {i18n.language === 'zh-TW' ? '適用說明' : 'Project compliance'}
                      </th>
                      <th style={{ width: '10%' }}>{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map((item) => (
                      <tr key={item.id}>
                        <td dangerouslySetInnerHTML={{ __html: item.applicability_codition || '-' }} />
                        <td dangerouslySetInnerHTML={{ __html: item.project_compliance || '-' }} />
                        <td className="actions">
                          {canUpdate && (
                            <button className="btn-edit" onClick={() => openModal(item)} style={{ marginRight: '12px' }}>
                              {t('common.edit')}
                            </button>
                          )}
                          {canDelete && (
                            <button className="btn-delete" onClick={() => handleDelete(item)}>
                              {t('common.delete')}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* 分頁控制 */}
              {filteredData.length > itemsPerPage && (
                <div className="pagination-container">
                  <div className="pagination-info">
                    <label>
                      {i18n.language === 'zh-TW' ? '每頁顯示：' : 'Items per page: '}
                      <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(parseInt(e.target.value)); setCurrentPage(1); }}>
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </select>
                      {i18n.language === 'zh-TW' ? '筆' : ''}
                    </label>
                    <span className="pagination-text">
                      {i18n.language === 'zh-TW'
                        ? `共 ${filteredData.length} 筆資料，第 ${currentPage} / ${totalPages} 頁`
                        : `Total ${filteredData.length} records, Page ${currentPage} / ${totalPages}`}
                    </span>
                  </div>
                  <div className="pagination-buttons">
                    <button className="btn-pagination" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>⟪</button>
                    <button className="btn-pagination" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>‹</button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        className={`btn-pagination ${currentPage === page ? 'active' : ''}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    ))}
                    <button className="btn-pagination" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>›</button>
                    <button className="btn-pagination" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>⟫</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!selectedProjectId && !loading && (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: '16px' }}>
          {i18n.language === 'zh-TW' ? '請先在上方選擇專案' : 'Please select a project from the top bar'}
        </div>
      )}

      {/* 新增/編輯 Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <div className="modal-header">
              <h2>
                {editingItem
                  ? (i18n.language === 'zh-TW' ? `編輯${activeTabLabel}` : `Edit ${activeTabLabel}`)
                  : (i18n.language === 'zh-TW' ? `新增${activeTabLabel}` : `Add ${activeTabLabel}`)}
              </h2>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                    {i18n.language === 'zh-TW' ? '適用要求' : 'Applicability condition of methodological regulatory document or methodological requirement specified by the host Party'} *
                  </label>
                  <RichTextEditor
                    value={formData.applicability_codition}
                    onChange={(html) => setFormData({ ...formData, applicability_codition: html })}
                    placeholder={i18n.language === 'zh-TW' ? '請輸入適用要求...' : 'Please enter applicability condition...'}
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                    {i18n.language === 'zh-TW' ? '適用說明' : 'Compliance of the project with the applicability condition'}
                  </label>
                  <RichTextEditor
                    value={formData.project_compliance}
                    onChange={(html) => setFormData({ ...formData, project_compliance: html })}
                    placeholder={i18n.language === 'zh-TW' ? '請輸入適用說明...' : 'Please enter project compliance...'}
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeModal}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn-primary">
                  {t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionB2Page;
